// src/services/sessionService.js
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { buildSessionApiUrl } from '../config/appConfig';

// Sessions API base URL - works in both local (via proxy) and production (direct)
const SESSIONS_API_URL = import.meta.env.VITE_SESSIONS_API_URL || 'https://dva-admin-backend-dev-813595575009.us-central1.run.app';

/**
 * Validates user token with backend and triggers session document creation
 * @param {string} token - JWT token from Firebase Auth
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Session validation response
 */
export const validateToken = async (token, userId) => {
  try {
    // Use absolute URL to work correctly in both local and production environments
    const url = `${SESSIONS_API_URL}/admin/sessions/${userId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Session validation failed: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      data: data.data || data,
      message: data.message,
    };
  } catch (error) {
    console.error('Session validation error:', error);
    throw error;
  }
};

/**
 * Subscribe to Firestore session document for real-time updates
 * @param {string} userId - User ID
 * @param {Function} onUpdate - Callback when session document updates
 * @param {Function} onError - Callback for errors
 * @returns {Function} Unsubscribe function
 */
export const subscribeToSession = (userId, onUpdate, onError) => {
  if (!userId) {
    console.warn('Cannot subscribe to session: userId is required');
    return () => {};
  }

  try {
    const sessionDocRef = doc(db, 'sessions', userId);

    const unsubscribe = onSnapshot(
      sessionDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const sessionData = {
            ...docSnapshot.data(),
            id: docSnapshot.id,
            exists: true,
          };
          onUpdate(sessionData);
        } else {
          onUpdate({ exists: false });
        }
      },
      (error) => {
        console.error('Session subscription error:', error);
        if (onError) {
          onError(error);
        }
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Failed to subscribe to session:', error);
    if (onError) {
      onError(error);
    }
    return () => {};
  }
};

/**
 * Get friendly error message for session errors
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export const getSessionErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';

  const errorMessage = error.message || error.toString();

  // API validation errors
  if (errorMessage.includes('401')) {
    return 'Session expired. Please sign in again.';
  }
  if (errorMessage.includes('403')) {
    return 'Access denied. Please contact support.';
  }
  if (errorMessage.includes('404')) {
    return 'Session service unavailable. Please try again.';
  }
  if (errorMessage.includes('500')) {
    return 'Server error during session validation. Please try again.';
  }
  if (errorMessage.includes('Network') || errorMessage.includes('Failed to fetch')) {
    return 'Network error. Please check your connection.';
  }

  // Default
  return 'Session validation failed. Please try signing in again.';
};

export default {
  validateToken,
  subscribeToSession,
  getSessionErrorMessage,
};
